"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Container,
  Card,
  CardBody,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAuth } from "../contexts/AuthContext";
import { Link as RouterLink, useLocation } from "react-router-dom";

const SignIn: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { signIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const { error } = await signIn(email, password);

      if (error) {
        setError(error.message || "Invalid email or password");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred during sign in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md">
        <Card boxShadow="xl" border="1px" borderColor="gray.200">
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Logo/Brand */}
              <Box textAlign="center">
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                  mx="auto"
                  mb={2}
                />
                <Text color="secondary.600" fontSize="sm">
                  AI Outreach Platform
                </Text>
              </Box>

              {/* Form */}
              <Box w="100%">
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel color="secondary.700">Email</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        bg="white"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: "primary.500",
                          boxShadow:
                            "0 0 0 1px var(--chakra-colors-primary-500)",
                        }}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="secondary.700">Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          bg="white"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "primary.500",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-primary-500)",
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {successMessage && (
                      <Alert status="success" borderRadius="md">
                        <AlertIcon />
                        {successMessage}
                      </Alert>
                    )}

                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      isLoading={isLoading}
                      loadingText="Signing in..."
                      w="100%"
                      variant="brandGradient"
                      size="lg"
                    >
                      Sign In
                    </Button>
                  </VStack>
                </form>
              </Box>

              {/* Reset Password Link */}
              <Box textAlign="center">
                <Link
                  as={RouterLink}
                  to="/password-reset"
                  color="primary.500"
                  fontSize="sm"
                  _hover={{ color: "primary.600", textDecoration: "underline" }}
                >
                  Forgot your password? Reset it here
                </Link>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default SignIn;
